import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse, pagedResponseWith } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * The stock layer's read surface + the two human write paths of INV-06:
 *
 *  - Stock levels (INV-05): per-item rows with the per-warehouse breakdown.
 *    available = on hand − reserved, computed (BR-INV-05-04); status derived
 *    out → low (on AVAILABLE) → over (on ON-HAND) → ok (BR-INV-05-01/-02);
 *    the summary cards ignore the filters (BR-INV-05-05).
 *
 *  - Stock movements (INV-06): the append-only ledger, read-only here except
 *    the manual adjustment (amber rows, ADJ-M#### — BR-INV-06-06/-07/-08)
 *    and the opposite-entry correction (BR-INV-06-02's only correction path,
 *    permission-gated on inventory.approve — "checked where it posts").
 *
 * Every OTHER movement is written by a document (voucher, count, transfer,
 * and the next wave's GRN/delivery) through the ONE writer service.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const STOCK_MESSAGES = {
    /**
     * BR-INV-04-08 / BR-INV-06-13 — the negative-stock refusal.
     *
     * The spec quotes this sentence twice, at ERR-MFG-02-08 and ERR-MFG-04-05,
     * and BOTH quotations carry a second half: "Only 140 LED Module 9W are in
     * Main Warehouse. You are trying to take out 200." The message used to stop
     * at the full stop, which tells a storeman there is a shortage and not by how
     * much — so the fix is to lower a number he has to go and look up.
     * `apps/api/test/spec-wording.spec.ts` holds the literal sentence.
     */
    insufficientStock: (onHand, itemName, warehouseName, wanted) => `Only ${onHand} ${itemName} are in ${warehouseName}. You are trying to take out ${wanted}.`,
    /**
     * BR-INV-04-08's second gate. The warehouse flag says the LOCATION tolerates
     * negative stock; `inventory.override_negative_stock` says the PERSON may
     * decide to. A confirmation that would take a line below zero needs both,
     * and this is the sentence for the half the role does not hold. Codes,
     * not names: the caller has the line's item code and warehouse code in hand
     * the way the grid shows them.
     */
    negativeStockNeedsPermission: (itemCode, warehouseCode) => `Posting would take ${itemCode} below zero in ${warehouseCode}. Only a role allowed to override negative stock can post it.`,
    /** BR-INV-06-06 / ERR-INV-06-01. */
    reasonRequired: 'A reason is required before saving.',
    /** BR-MD-03-07 — a Service never appears in a stock movement. */
    serviceNeverStocked: (name) => `${name} is a service and never appears in a stock movement.`,
    /** BR-MD-03-08's shape for stock actions on a switched-off item. */
    itemInactive: (name) => `${name} is switched off and cannot be picked.`,
    /** Correcting a movement that a later correction already covers. */
    alreadyCorrected: (movementNo) => `${movementNo} has already been corrected. Correct the correction instead.`,
    // ── BR-MD-03 tracking at document time (the -17 family) ──────────────────
    /**
     * ERR-MD-03-16's shape: a serial-tracked line names one serial per unit.
     *
     * The count is a NUMBER (it is `serials.length !== count`'s count — always
     * a whole number of units, `serialWholeUnits` refuses anything else first).
     * It used to arrive pre-stringified; D46 widened it to the raw integer so
     * the Arabic dictionary can pluralise "serial numbers" on it. `String(n)`
     * of a whole number is the same bytes the callers always passed, so the
     * composed sentence is unchanged.
     */
    serialsRequired: (qty, name) => `Pick ${qty} serial numbers for ${name} before posting.`,
    /** Serial units are indivisible — a fractional serial quantity is nonsense. */
    serialWholeUnits: (name) => `${name} is serial-tracked — quantities must be whole units.`,
    /** One physical unit cannot appear twice on one document. */
    serialDuplicate: (serial) => `Serial ${serial} appears more than once on this document.`,
    /** Uniqueness in the tenant: a serial on the shelf cannot arrive again. */
    serialAlreadyInStock: (serial, name) => `Serial ${serial} of ${name} is already in stock.`,
    /** An outbound serial must be on the shelf it claims to leave from. */
    serialNotInStock: (serial, name, warehouse) => `Serial ${serial} of ${name} is not in ${warehouse}.`,
    /** A batch-tracked line names its lot. */
    batchRequired: (name) => `Type a batch / lot number for ${name} before posting.`,
};
/**
 * D46 — the stock layer's structured refusals: key + params beside the
 * composed English sentence, so a refusal that names an item, a serial, a
 * warehouse or a quantity reaches an Arabic reader in Arabic. One builder per
 * parameterised member the API raises inside an HTTP exception body — the
 * raise sites throw THESE, and each `message` is composed by the
 * `STOCK_MESSAGES` twin so the two cannot drift (parity pinned member by
 * member in `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `reasonRequired` is deliberately NOT here: it is a fixed sentence, not a
 * parameterised one — the exact-match map (`mv.errReasonRequired`) already
 * serves it and D46 leaves unparameterised sentences to that map unchanged.
 *
 * Every param is a display value (a name, a serial, a code, a quantity
 * string); none is a select token. `serialsRequired.qty` is the family's one
 * COUNT and travels RAW — the en dictionary interpolates it bare (that is
 * the English sentence's own shape) and ar carries the full six-category
 * plural set on it. `insufficientStock`'s two quantities stay STRINGS: they
 * are decimal quantities in the item's own unit precision (`7.5` is a legal
 * on-hand), not counts, so nothing may pluralise on them.
 */
export const STOCK_REFUSALS = {
    insufficientStock: (onHand, itemName, warehouseName, wanted) => ({
        message: STOCK_MESSAGES.insufficientStock(onHand, itemName, warehouseName, wanted),
        key: 'stock.errInsufficientStock',
        params: { onHand, itemName, warehouseName, wanted },
    }),
    negativeStockNeedsPermission: (itemCode, warehouseCode) => ({
        message: STOCK_MESSAGES.negativeStockNeedsPermission(itemCode, warehouseCode),
        key: 'stock.errNegativeStockNeedsPermission',
        params: { itemCode, warehouseCode },
    }),
    serviceNeverStocked: (name) => ({
        message: STOCK_MESSAGES.serviceNeverStocked(name),
        key: 'stock.errServiceNeverStocked',
        params: { name },
    }),
    itemInactive: (name) => ({
        message: STOCK_MESSAGES.itemInactive(name),
        key: 'stock.errItemInactive',
        params: { name },
    }),
    alreadyCorrected: (movementNo) => ({
        message: STOCK_MESSAGES.alreadyCorrected(movementNo),
        key: 'stock.errAlreadyCorrected',
        params: { movementNo },
    }),
    serialsRequired: (qty, name) => ({
        message: STOCK_MESSAGES.serialsRequired(qty, name),
        key: 'stock.errSerialsRequired',
        params: { qty, name },
    }),
    serialWholeUnits: (name) => ({
        message: STOCK_MESSAGES.serialWholeUnits(name),
        key: 'stock.errSerialWholeUnits',
        params: { name },
    }),
    serialDuplicate: (serial) => ({
        message: STOCK_MESSAGES.serialDuplicate(serial),
        key: 'stock.errSerialDuplicate',
        params: { serial },
    }),
    serialAlreadyInStock: (serial, name) => ({
        message: STOCK_MESSAGES.serialAlreadyInStock(serial, name),
        key: 'stock.errSerialAlreadyInStock',
        params: { serial, name },
    }),
    serialNotInStock: (serial, name, warehouse) => ({
        message: STOCK_MESSAGES.serialNotInStock(serial, name, warehouse),
        key: 'stock.errSerialNotInStock',
        params: { serial, name, warehouse },
    }),
    batchRequired: (name) => ({
        message: STOCK_MESSAGES.batchRequired(name),
        key: 'stock.errBatchRequired',
        params: { name },
    }),
};
// ── The movement kinds: INV-06 §5.3's eleven, plus PART 9 §9.5's two ────────
export const StockMovementKindSchema = z.enum([
    'sale',
    'purchase',
    'transfer_in',
    'transfer_out',
    'adjustment_in',
    'adjustment_out',
    'stock_take',
    'customer_return',
    'supplier_return',
    'bundle_assembly',
    'bundle_disassembly',
    // PART 9 §9.5 (20260814010000). `consumption` is the ONLY kind that goes
    // both ways: a Return-to-Stock issue and every correction put parts back on
    // the shelf under it, because they are the same event running backwards.
    // `production_output` only ever goes in — BR-MFG-02-16 receives good output
    // and receives scrap nowhere at all.
    'consumption',
    'production_output',
    // Wave 5C (20261027300000): a value-only cost adjustment — an import
    // order's landed charges posted onto a goods receipt's stock. Zero units.
    'landed_cost',
]);
export const StockReferenceKindSchema = z.enum([
    'manual',
    'stock_adjustment',
    'stock_count',
    'stock_transfer',
    'goods_receipt',
    'delivery_note',
    'invoice',
    'credit_note',
    'purchase_return',
    'work_order',
    'kit',
    // SAL-06: the till's own documents. INV-06's movement ledger names what
    // CAUSED each movement, so a counter sale must be traceable as itself
    // rather than borrowing 'invoice' — see 20260810030000.
    'pos_sale',
    'pos_refund',
    // MFG-04: MC-#### is its own document, and an Indirect issue names no work
    // order at all (BR-MFG-04-10), so those rows cannot borrow 'work_order'.
    'consumption_voucher',
    // A standalone purchase invoice that added goods to stock directly, when the
    // inventory.bill_writes_stock setting is on (20260927110000).
    'purchase_invoice',
]);
/** BR-INV-05-01's derived vocabulary — never stored. */
export const StockStatusSchema = z.enum(['ok', 'low', 'out', 'over']);
// ── Stock levels (INV-05) ───────────────────────────────────────────────────
/**
 * One bin chip of the §5.5 breakdown — `A-01-01 · 180`. The quantity is
 * Σ stock_movements.qty for that item+warehouse+bin, computed in SQL: the
 * SAME ledger rows whose sum is the warehouse's on-hand, partitioned by bin.
 * That derivation is BR-INV-05-11's enforcement — there is no stored bin
 * balance anywhere that COULD disagree with on-hand, which is exactly the
 * disagreement the prototype's hard-coded chips made possible.
 */
export const StockLevelBinRowSchema = z
    .object({
    binId: z.uuid(),
    binCode: z.string(),
    qty: z.string(),
})
    .strict();
/**
 * One batch/lot chip of the §5.5 breakdown — `LOT-2405 · 400`. Same ledger,
 * partitioned by lot instead of bin (BR-INV-05-11 "and batch quantities
 * likewise"). For a batch-tracked item the partition is TOTAL — tracking.ts
 * refuses any movement of one that names no lot — so these chips sum to the
 * warehouse on-hand exactly. (§5.5's `exp. 2028-01` tail is not carried: the
 * build stores no per-lot expiry anywhere, and a chip must not invent one.)
 */
export const StockLevelBatchRowSchema = z
    .object({
    batchNo: z.string(),
    qty: z.string(),
})
    .strict();
export const StockLevelWarehouseRowSchema = z
    .object({
    warehouseId: z.uuid(),
    warehouseCode: z.string(),
    warehouseName: z.string(),
    onHand: z.string(),
    reserved: z.string(),
    /** on hand − reserved, computed (BR-INV-05-04). */
    available: z.string(),
    avgUnitCost: z.string(),
    stockValue: z.string(),
    /**
     * BR-INV-05-11 — the §5.5 strips, ledger-derived (see the two row schemas
     * above). Zero-balance buckets are dropped: a bin the stock has left is
     * not "where the item is". Un-binned stock (INV-08 field 5's bin is
     * OPTIONAL — "Any / unassigned") belongs to no chip, so Σ bins ≤ on-hand
     * with equality when every movement named its bin; Σ batches = on-hand
     * whenever the item is batch-tracked.
     */
    bins: StockLevelBinRowSchema.array(),
    batches: StockLevelBatchRowSchema.array(),
})
    .strict();
export const StockLevelRowSchema = z
    .object({
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    /** S5: the Arabic twin; `localizedName()` picks it on an Arabic screen. */
    itemNameAr: z.string().nullable(),
    categoryName: z.string(),
    unitName: z.string(),
    unitSymbol: z.string(),
    /** N-4: the decimals this item's stock unit allows. */
    unitPrecision: UnitPrecisionSchema,
    isActive: z.boolean(),
    reorderPoint: z.string(),
    /** PART 8 §8.1: "the line that turns a stock row blue" — set on MD-03; null = no ceiling. */
    overstockCeiling: z.string().nullable(),
    onHand: z.string(),
    reserved: z.string(),
    available: z.string(),
    stockValue: z.string(),
    /** out → low (available ≤ reorder) → over (on hand ≥ ceiling) → ok. */
    status: StockStatusSchema,
    warehouses: StockLevelWarehouseRowSchema.array(),
})
    .strict();
/** BR-INV-05-05: computed over ALL items; never responds to the filter bar. */
export const StockSummarySchema = z
    .object({
    itemsTracked: z.number().int(),
    totalStockValue: z.string(),
    lowCount: z.number().int(),
    outCount: z.number().int(),
    /** Stock-bearing warehouses (excludes virtual + inactive) — INV-05 §5.1 card 5. */
    warehouseCount: z.number().int(),
})
    .strict();
// ── Stock movements (INV-06) ────────────────────────────────────────────────
export const StockMovementRowSchema = z
    .object({
    id: z.uuid(),
    /** MV-###### — display formatting of the continuous sequence. */
    code: z.string(),
    movementNo: z.number().int(),
    occurredAt: z.string(),
    kind: StockMovementKindSchema,
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    binCode: z.string().nullable(),
    batchNo: z.string().nullable(),
    /** Signed, in the item's stock unit — outbound negative. */
    qty: z.string(),
    unitSymbol: z.string(),
    /** N-4: the decimals that stock unit allows. */
    unitPrecision: UnitPrecisionSchema,
    unitCost: z.string(),
    value: z.string(),
    /** Per item PER WAREHOUSE (BR-INV-06-05). */
    balanceAfter: z.string(),
    referenceKind: StockReferenceKindSchema,
    referenceId: z.uuid().nullable(),
    referenceNo: z.string(),
    /** The amber row (BR-INV-06-09). */
    isManual: z.boolean(),
    correctsMovementId: z.uuid().nullable(),
    performedByName: z.string(),
    reason: z.string().nullable(),
})
    .strict();
/** INV-06 §5.1 — these cards RESPOND to the filters. */
export const StockMovementSummarySchema = z
    .object({
    totalMovements: z.number().int(),
    /**
     * BR-INV-06-15: quantities are never added across units — netQty is only
     * present when every filtered row shares one unit; otherwise null and
     * the money net is the headline.
     */
    netQty: z.string().nullable(),
    /**
     * N-4 for that net: the one unit every filtered row shares, so the figure
     * can be written with its decimals and its symbol. Null exactly when
     * `netQty` is — a net across units has no unit to name (N-14), which is
     * why the two are decided by the same test.
     */
    netQtyUnit: z.object({ symbol: z.string(), precision: UnitPrecisionSchema }).strict().nullable(),
    netValue: z.string(),
    byGroup: z
        .object({
        sales: z.number().int(),
        purchases: z.number().int(),
        transfers: z.number().int(),
        adjustments: z.number().int(),
        returns: z.number().int(),
        bundles: z.number().int(),
    })
        .strict(),
})
    .strict();
export const ManualAdjustmentRequestSchema = z
    .object({
    itemId: z.uuid(),
    warehouseId: z.uuid(),
    binId: z.uuid().nullable().optional(),
    /** Always a positive magnitude; the direction supplies the sign (BR-INV-06-07). */
    qty: QtyStringSchema,
    direction: z.enum(['increase', 'decrease']),
    /** BR-INV-06-06 — required, non-blank, enforced in the handler. */
    reason: z.string().trim().min(1, STOCK_MESSAGES.reasonRequired).max(500),
    /**
     * BR-MD-03-17, the same capture shape as every document: a tracked item
     * names its units by hand too. A hand-written adjustment is the one place
     * a serial can enter or leave the ledger with no document behind it, so
     * it is exactly the place an unnamed unit would be lost for good.
     */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
})
    .strict();
export const CorrectMovementRequestSchema = z
    .object({
    /** Required — a correction is a manual movement and manual movements carry reasons. */
    reason: z.string().trim().min(1, STOCK_MESSAGES.reasonRequired).max(500),
})
    .strict();
export const stockContract = c.router({
    levels: {
        method: 'GET',
        path: '/api/stock/levels',
        query: pagedQuery({
            /** Matches item code, name or barcode-when-it-exists (BR-INV-05-09). */
            q: z.string().optional(),
            status: StockStatusSchema.optional(),
            /** Comma-separated warehouse ids — any-of (BR-INV-05-06). */
            warehouseIds: z.string().optional(),
            /** bugs: ST-02 — includes the category's DESCENDANTS, as the Items list does. */
            categoryId: z.uuid().optional(),
            /** bugs: ST-02 — items of one maker (IT-S6). */
            manufacturerId: z.uuid().optional(),
            /** bugs: Stock levels — Tracked / Untracked on `trackStockAlerts` (IT-L3). Absent = all. */
            alerts: z.enum(['tracked', 'untracked']).optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
            /**
             * BR-INV-05-08 — §5.2 field 13's three ADDED filters, which "must
             * actually filter the rows". ERR-INV-05-02 was the prototype's §14
             * symptom ("the chip appears and nothing filters"): its filters were
             * collected and never applied. These three are applied in the same
             * SQL as the other four — a value here that changed nothing would be
             * that defect back.
             */
            supplierId: z.uuid().optional(),
            /** items.tax_category is free text (MD-03) — matched whole, case-insensitively. */
            taxCategory: z.string().optional(),
            unitId: z.uuid().optional(),
        }),
        responses: {
            200: pagedResponseWith('rows', StockLevelRowSchema, {
                /**
                 * Computed over everything — never filtered (BR-INV-05-05), and so
                 * never the page's arithmetic either. The OPPOSITE of INV-06's
                 * cards, and the difference is the spec's.
                 */
                summary: StockSummarySchema,
            }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Stock balances (inventory.view), PAGED (NFR-42): per-item rows + per-warehouse breakdown; status derived out → low(available) → over(on-hand) → ok (BR-INV-05-01/-02).',
    },
    itemMovements: {
        method: 'GET',
        path: '/api/stock/items/:itemId/movements',
        pathParams: z.object({ itemId: z.uuid() }).strict(),
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('rows', StockMovementRowSchema),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Movement history of ONE item, newest first (BR-INV-05-10 — filtered to the clicked item), PAGED (NFR-42): the same table and the same growth as INV-06, narrowed by item rather than bounded by it — a fast-moving SKU accrues thousands of rows a year on its own.',
    },
    movements: {
        method: 'GET',
        path: '/api/stock/movements',
        query: pagedQuery({
            q: z.string().optional(),
            reference: z.string().optional(),
            /** Both bounds inclusive (BR-INV-06-16). */
            dateFrom: z.iso.date().optional(),
            dateTo: z.iso.date().optional(),
            /** Comma-separated kinds; empty = all eleven. */
            kinds: z.string().optional(),
            warehouseIds: z.string().optional(),
            categoryId: z.uuid().optional(),
        }),
        responses: {
            200: pagedResponseWith('rows', StockMovementRowSchema, {
                /**
                 * These cards respond to the filters (INV-06 §5.1) — and to the
                 * FILTERS, never to the page. `summary.totalMovements` and `total`
                 * are the same number by construction, computed once.
                 */
                summary: StockMovementSummarySchema,
            }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The movement ledger (inventory.view), newest first, PAGED (NFR-42) — the worst screen in the acceptance sweep at 54.9 s. Read-only: no edit, no delete, for any role, and none for the Owner either (BR-INV-06-02).',
    },
    manualAdjust: {
        method: 'POST',
        path: '/api/stock/movements/manual-adjustment',
        body: ManualAdjustmentRequestSchema,
        responses: {
            /**
             * ROWS, not a row. One adjustment of a serial-tracked item is one
             * movement PER SERIAL — that is how the ledger answers "where is
             * serial X" — so the shape that could only ever carry one row was
             * wrong for the tracked case and stayed silent about it. The
             * untracked adjustment, which is nearly all of them, returns a
             * one-element array.
             */
            201: z.object({ rows: StockMovementRowSchema.array() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Manual adjustment (inventory.edit): positive magnitude + direction + required reason (BR-INV-06-06/-07); writes amber ADJ-M#### movements (BR-INV-06-08/-09) — one per serial for a tracked item (BR-MD-03-17) — and posts 5030↔1131 at the current location cost.',
    },
    correct: {
        method: 'POST',
        path: '/api/stock/movements/:movementId/correct',
        pathParams: z.object({ movementId: z.uuid() }).strict(),
        body: CorrectMovementRequestSchema,
        responses: {
            201: StockMovementRowSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Opposite-entry correction (inventory.approve — BR-INV-06-02: a movement is never edited; INV-06 §9: the correcting movement is a permission checked where it posts). Mirrors the movement, links correctsMovementId.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=stock.js.map